//@import dependencies
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
//@end

//@import components
import AppContainer from 'app_components/layouts/app'
import BreadcrumbComponent from 'app_components/commons/breadcrumb'
import DataTableComponent from 'app_components/commons/dataTable'
import CourseSchedulingDetailsForm from 'app_components/modules/admin/courseScheduling/details/courseSchedulingDetailsForm'
import CourseSchedulingDetailsSkeleton from './courseSchedulingDetailsSkeleton'
import CourseSchedulingDetailListModule from 'app_components/modules/courseSchedulingDetailList'
//@end

// @import hooks
// @end

// @INFO Import types
import { IPagination } from 'app_types/pagination/paginationTypes'
import { ICourseSchedulingDetailListItem } from 'app_types/admin/courseScheduling/courseSchedulingDetailsTypes'
import { ICourseSchedulingListItem } from 'app_types/admin/courseScheduling/courseSchedulingTypes'
// @end

// @INFO Import services
// @end

// @INFO Import Styles
import './../courseSchedulingDetailsScreen.scss'
import CertificationControlWrapper from '../../../../commons/CertificationControlWrapper/CertificationControlWrapper';
import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import { AiTwotoneSafetyCertificate } from 'react-icons/ai'
import { Link, useParams } from 'react-router-dom'
import { routingService } from 'app_services/routingService'
import UserService from 'app_services/user/userService'
import { BsDownload } from 'react-icons/bs'
import CourseSchedulingService from 'app_services/admin/courseScheduling/courseSchedulingService'

// @ADD Component types
interface ICourseSchedulingDetailsScreenLayoutProps {
  itemList: ICourseSchedulingDetailListItem[]
  courseScheduling: ICourseSchedulingListItem | null
  status: null | 'loading' | 'error'
  add: boolean
  edit: boolean
  view: boolean
  editData: any
  selectedRow: null | string
  pagination: IPagination
  addItem: any
  updateItem: any
  scheduling: string[]
  showAddForm: (add: boolean) => void
  showEditForm: (edit: boolean, editData?: any) => void
  onViewerRow: (view: boolean, editData?: any) => void
  onPageChange: (page: number) => void
  onRowClick: (row: any) => void
  removeItem: (userId: string) => void
  onSearch: (search: string) => void
}

const CourseSchedulingDetailsScreenLayout: React.FC<ICourseSchedulingDetailsScreenLayoutProps> = (props) => {

  const { t } = useTranslation()
  const { course_scheduling } = useParams<{ course_scheduling: string }>()
  const userService = useMemo(() => new UserService(), [])
  const [loadingReport, setLoadingReport] = useState<boolean>(false)
  const courseSchedulingService = useMemo(() => new CourseSchedulingService(), [])

  const handleClickGenerateReport = useCallback(async () => {
    try {
      setLoadingReport(true)
      const report = await courseSchedulingService.generatePdfReport({
        course_scheduling,
        type: 'single',
        format: 'pdf',
      })
      if (report?.path) {
        window.open(report?.path, '_blank', 'noreferrer')
      }
      setLoadingReport(false)
    } catch (error) {
      setLoadingReport(false)
    }
  }, [courseSchedulingService, course_scheduling])

  return(
    <AppContainer>
      <div className='course_scheduling_details-component container-fluid'>
        <BreadcrumbComponent
          links={[
            '/admin/course-scheduling',
            '#',
          ]}
          texts={[
            `${t('components.breadcrumb.course_scheduling')}`,
            `${t('components.breadcrumb.course_scheduling_details')}`
          ]}
        />

        <div className='course-scheduling-details-screen'>

          {props.status === 'loading' && (
            <CourseSchedulingDetailsSkeleton />
          )}

          {props.status !== 'loading' && props.courseScheduling && !props.add && !props.edit && !props.view && (
            <>
              <h2>{t('screens.course_scheduling.details.title', {name: props.courseScheduling.program.name})}</h2>
              <h4>ID de Servicio: {props.courseScheduling?.metadata.service_id}</h4>
              <h4>Código del programa: {props.courseScheduling?.program?.code}</h4>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <ButtonComponent
                  onClick={handleClickGenerateReport}
                  loading={loadingReport}
                  variant='primary'
                >
                  <ButtonComponent.Icon>
                    <BsDownload />
                  </ButtonComponent.Icon>
                  Generar reporte
                </ButtonComponent>
                {userService.can('permission:course_scheduling_certificate_multiple_configuration', 'module:course_scheduling') && (
                  <CertificationControlWrapper
                    courseScheduling={props.courseScheduling}
                    showOnMultipleCertificate
                  >
                    <Link to={routingService.getSecurePath(`/admin/certificate-settings/${course_scheduling}`)}>
                      <ButtonComponent variant='primary'>
                        <ButtonComponent.Icon>
                          <AiTwotoneSafetyCertificate />
                        </ButtonComponent.Icon>
                        Certificados
                      </ButtonComponent>
                    </Link>
                  </CertificationControlWrapper>
                )}
              </div>
              <DataTableComponent
                data={props.itemList}
                headerKeys={[
                  `${t('components.dataTable.headers.course_scheduling.details.course_code')}`,
                  `${t('components.dataTable.headers.course_scheduling.details.course')}`,
                  `${t('components.dataTable.headers.course_scheduling.details.duration')}`,
                  // `${t('components.dataTable.headers.course_scheduling.details.schedulingMode')}`,
                  `${t('components.dataTable.headers.course_scheduling.details.teacher')}`,
                  `${t('components.dataTable.headers.course_scheduling.details.startDate')}`,
                  `${t('components.dataTable.headers.course_scheduling.details.endDate')}`,
                ]}
                keys={['course.code','course.name', 'duration_formated', 'teacher.fullname', 'startDate', 'endDate']}
                selectedRow={props.selectedRow}
                pagination={props.pagination}
                detailsKey={[
                  // { name: `${t('components.dataTable.details.course_scheduling.observations')}`, key: 'observations' },
                  // { name: `${t('components.dataTable.details.course_scheduling.service_id')}`, key: 'metadata.service_id' },
                  // { name: `${t('components.dataTable.details.course_scheduling.coordinator')}`, key: 'metadata.user.fullname' },
                  // { name: `${t('components.dataTable.details.course_scheduling.service_date')}`, key: 'metadata.date' },
                  // { name: `${t('components.dataTable.details.course_scheduling.service_year')}`, key: 'metadata.year' },
                ]}
                // onSearch={props.onSearch}
                onAddRow={props.courseScheduling?.schedulingStatus?.name === 'Cancelado' ? undefined : props.showAddForm}
                onClickRow={props.onRowClick}
                onRemoveRow={props.courseScheduling?.schedulingStatus?.name === 'Cancelado' ? undefined : props.removeItem}
                onPageChange={props.onPageChange}
                onEditRow={props.showEditForm}
                onViewerRow={props.onViewerRow}
                // onUpload={handleUploadBulkModal}
                onModal={null}
              />
            </>
          )}

          {props.add && props.courseScheduling && (
            <div className='row'>
              <div className="course-scheduling-details-container-form col-6 mb-3">
                <div className="d-flex align-items-center mb-2">
                  <h2 className="form-title">
                    {t("screens.course_scheduling.details.add_layout.title_page", {name: props.courseScheduling.program.name})}
                  </h2>
                  <button className="btn btn-light btn-sm border ml-2" onClick={() => props.showAddForm(false)}>
                    {t("buttons.back")}
                  </button>
                </div>
                <CourseSchedulingDetailsForm
                  onHandleSubmit={props.addItem}
                  viewer={false}
                  courseScheduling={props.courseScheduling}
                  scheduling={props.scheduling}
                />
              </div>
              <div className='col-6 mb-3'>
                <CourseSchedulingDetailListModule courseScheduling={props.courseScheduling} />
              </div>
            </div>
          )}

          {(props.edit || props.view) && props.editData && props.courseScheduling && (
            <div className='row'>
              <div className="course-scheduling-details-container-form col-6 mb-3">
                <div className="d-flex align-items-center mb-2">
                  {props.edit && (
                    <>
                      <h2 className="form-title">
                        {t("screens.course_scheduling.details.edit_layout.title_page", {name: props.courseScheduling.program.name})}
                      </h2>
                      <button className="btn btn-light btn-sm border ml-2" onClick={() => props.showEditForm(false)}>
                        {t("buttons.back")}
                      </button>
                    </>
                  )}
                  {props.view && (
                    <>
                      <h2 className="form-title">
                        {t("screens.course_scheduling.details.view_layout.title_page")}
                      </h2>
                      <button className="btn btn-light btn-sm border ml-2" onClick={() => props.onViewerRow(false)}>
                        {t("buttons.back")}
                      </button>
                    </>
                  )}
                </div>
                <CourseSchedulingDetailsForm
                  editData={props.editData}
                  onHandleSubmit={props.updateItem}
                  viewer={props.courseScheduling?.schedulingStatus?.name === 'Cancelado' || props.view}
                  courseScheduling={props.courseScheduling}
                  scheduling={props.scheduling}
                />
              </div>
              <div className='col-6 mb-3'>
                <CourseSchedulingDetailListModule courseScheduling={props.courseScheduling} />
              </div>
            </div>
          )}
        </div>
      </div>
    </AppContainer>
  )
}

export default CourseSchedulingDetailsScreenLayout
